import type { FieldProps } from 'formik';
import { Field as FormikField } from 'formik';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Field } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Pill from '../../components/Pill';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated/gql';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import TopicPills from '../../topic/TopicPills';
import useAllTopics from '../../topic/useAllTopics';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import * as styles from './GithubRepoTopicEditor.module.scss';

const GITHUB_REPOS_QUERY = gql(/** @complexity 4 */ `
  query getGithubRepos {
    repos: gitRepos(platform: GITHUB) {
      owner
      name
    }
  }
`);

const SEARCH_CONFIG = { topic: {} };

interface GithubRepoTopicEditorProps {
  name: string;
}

const GithubRepoTopicEditor = ({
  name: fieldName,
}: GithubRepoTopicEditorProps) => {
  const { data, loading } = useQuery(GITHUB_REPOS_QUERY);
  const repos = data?.repos ?? [];
  const topics = useAllTopics({ flatten: true });
  const [topicEditingAt, setTopicEditingAt] = useState<string | null>(null);

  if (loading) {
    return <LoadingIndicator delay={0} />;
  }

  if (!repos.length) {
    return <>No GitHub repo found.</>;
  }

  return (
    <>
      {repos.map(({ owner, name: repoName }) => {
        const fullName = `${owner}/${repoName}`;
        const isEditing = topicEditingAt === fullName;

        return (
          <div className={styles.repoRow} key={fullName}>
            <div className={styles.checkbox}>
              <Field
                label={
                  <span className={styles.label} title={fullName}>
                    {fullName}
                  </span>
                }
                margin="none"
                name={fieldName}
                type="checkbox"
                value={fullName}
              />
            </div>
            <FormikField name={`topicIdMap.${fullName}`}>
              {({
                field: { name, value },
                form: { values, setFieldValue },
              }: FieldProps<string>) => {
                if (!values[fieldName].includes(fullName)) {
                  return null;
                }

                const topic = value && topics.find(({ id }) => id === value);

                return (
                  <>
                    <div className={styles.arrow}>
                      <SVGIcon inline size="small">
                        {arrowRightIcon}
                      </SVGIcon>
                    </div>
                    {isEditing ? (
                      <div className={styles.select}>
                        <ClickOutsideHandler
                          onClickOutside={() => setTopicEditingAt(null)}
                        >
                          <EntitySelect
                            autoFocus
                            config={SEARCH_CONFIG}
                            onChange={(val) => {
                              if (val) {
                                setFieldValue(name, val.id);
                                setTopicEditingAt(null);
                              }
                            }}
                            styles={{
                              menu: (base) => ({ ...base, width: 280 }),
                            }}
                          />
                        </ClickOutsideHandler>
                      </div>
                    ) : (
                      <div className={styles.value}>
                        {topic ? (
                          <TopicPills
                            topics={[topic]}
                            size="small"
                            onRemove={() => {
                              setFieldValue(name, null);
                              setTopicEditingAt(fullName);
                            }}
                          />
                        ) : (
                          <Pill
                            onClick={() => setTopicEditingAt(fullName)}
                            size="small"
                            type="external"
                          >
                            Uncategorized
                          </Pill>
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </FormikField>
          </div>
        );
      })}
    </>
  );
};

export default GithubRepoTopicEditor;
